// src/App.tsx
import React, { useState } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import './index.css';

// Import all components
import SimpleDashboard from './components/SimpleDashboard';
import CustomerSearch from './components/CustomerSearch';
import CustomerDetail from './components/CustomerDetail';
import TicketDetail from './components/TicketDetail';
import EventTimeline from './components/EventTimeline';
import { Customer } from './services/MockDataService';

// ---- Amplify config (fail fast if missing) ----
const region = import.meta.env.VITE_AWS_REGION;
const userPoolId = import.meta.env.VITE_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;

if (!region || !userPoolId || !userPoolClientId) {
  throw new Error(
    'Missing required Amplify env vars: VITE_AWS_REGION, VITE_USER_POOL_ID, VITE_USER_POOL_CLIENT_ID'
  );
}

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId,
      userPoolClientId,
      loginWith: {
        email: true,
        phone: false,
      },
    },
  },
});

// Navigation state types
type View = 'dashboard' | 'customer-search' | 'customer-detail' | 'ticket-detail' | 'events';

interface NavigationState {
  view: View;
  customerPhone?: string;
  ticketId?: string;
  eventFilter?: string;
}

const AdminApp: React.FC<{ signOut: () => void; user: any }> = ({ signOut, user }) => {
  const [navigationState, setNavigationState] = useState<NavigationState>({
    view: 'dashboard'
  });

  // Navigation helpers
  const goToDashboard = () => {
    setNavigationState({ view: 'dashboard' });
  };

  const goToCustomerSearch = () => {
    setNavigationState({ view: 'customer-search' });
  };

  const goToCustomerDetail = (phoneNumber: string) => {
    setNavigationState({ 
      view: 'customer-detail', 
      customerPhone: phoneNumber 
    });
  };

  const goToTicketDetail = (ticketId: string) => {
    setNavigationState({ 
      view: 'ticket-detail', 
      ticketId 
    });
  };

  const goToEvents = (phoneNumber?: string) => {
    setNavigationState({ 
      view: 'events', 
      eventFilter: phoneNumber 
    });
  };

  const goBack = () => {
    // Smart back navigation
    switch (navigationState.view) {
      case 'customer-detail':
        goToCustomerSearch();
        break;
      case 'ticket-detail':
        if (navigationState.customerPhone) {
          goToCustomerDetail(navigationState.customerPhone);
        } else {
          goToCustomerSearch();
        }
        break;
      case 'events':
        if (navigationState.customerPhone) {
          goToCustomerDetail(navigationState.customerPhone);
        } else {
          goToDashboard();
        }
        break;
      default:
        goToDashboard();
    }
  };

  // Render current view
  const renderCurrentView = () => {
    switch (navigationState.view) {
      case 'dashboard':
        return (
          <SimpleDashboard 
            onCustomerSearchClick={goToCustomerSearch}
            onEventsClick={goToEvents}
            user={user}
            signOut={signOut}
          />
        );

      case 'customer-search':
        return (
          <CustomerSearch 
            onCustomerSelect={(customer: Customer) => goToCustomerDetail(customer.phone_number)}
          />
        );

      case 'customer-detail':
        return navigationState.customerPhone ? (
          <CustomerDetail 
            phoneNumber={navigationState.customerPhone}
            onTicketSelect={(ticketId: string) => {
              // Remember customer phone for smart back navigation
              setNavigationState({ 
                view: 'ticket-detail', 
                ticketId, 
                customerPhone: navigationState.customerPhone 
              });
            }}
            onEventsClick={() => goToEvents(navigationState.customerPhone)}
          />
        ) : (
          <div className="p-8 text-center">
            <p>No customer phone number provided</p>
            <button onClick={goBack} className="mt-4 text-blue-600">← Go Back</button>
          </div>
        );

      case 'ticket-detail':
        return navigationState.ticketId ? (
          <TicketDetail 
            ticketId={navigationState.ticketId}
            onCustomerSelect={goToCustomerDetail}
          />
        ) : (
          <div className="p-8 text-center">
            <p>No ticket ID provided</p>
            <button onClick={goBack} className="mt-4 text-blue-600">← Go Back</button>
          </div>
        );

      case 'events':
        return (
          <EventTimeline 
            phoneNumber={navigationState.eventFilter}
            onCustomerSelect={goToCustomerDetail}
            onTicketSelect={goToTicketDetail}
          />
        );

      default:
        return <SimpleDashboard onCustomerSearchClick={goToCustomerSearch} onEventsClick={goToEvents} user={user} signOut={signOut} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderCurrentView()}
    </div>
  );
};

const App = () => {
  return (
    <Authenticator
      hideSignUp={true}
      variation="modal"
      components={{
        Header() {
          return (
            <div className="text-center p-4">
              <h1 className="text-2xl font-bold text-gray-900">
                🔐 Lookey Admin Portal
              </h1>
              <p className="text-gray-600 mt-2">
                Secure admin access with MFA protection
              </p>
            </div>
          );
        },
      }}
    >
      {({ signOut, user }) => (
        <AdminApp signOut={() => signOut?.()} user={user} />
      )}
    </Authenticator>
  );
};

export default App;